import { get } from 'lodash';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import gaLogger from 'src/utils/ga-logger';
import { UitkButtonSize, UitkSecondaryButton } from 'uitk-react-button';
import { UitkCard } from 'uitk-react-cards';
import { UitkHeaderBrandLogo } from 'uitk-react-header-brand-logo';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { checkValue, mcicidFormatText, appendMcicid } from '../../../../helper';
import { FlightDealCard } from './flight-card';
import { FlightDealCardMobile } from './flight-card-mobile';
import { FlightHighLightsProps, FlightLoadMoreProps, FlightsViewsProps, FlightViewProps } from './types.d';

export const FlightDealsCards: React.FC<FlightsViewsProps> = inject('context')(
  observer((props) => {
    console.log('FlightDealsCards', props);

    let flightsCount = props?.initialFlightsCount;
    const maxFlightsCount = checkValue(props?.flightDealsProps?.noOfFlights)
      ? Number(props?.flightDealsProps?.noOfFlights)
      : 18;

    const { data, flightDealsProps } = props;
    const {
      title,
      viewMoreCtaText,
      mcicidTemplateType,
      mcicidCampaignUrl,
      mcicidReferrerId,
      mcicidMainUrl,
      flightBookCtaCopy,
      carrierLogo,
      carrierName,
    } = flightDealsProps;

    let loadAllFlights = get(data, 'flightsSearch.listingResult.listings', []).filter(
      (dt) => dt?.__typename === 'FlightsStandardOffer'
    );

    const minTransitDurationHr = checkValue(props?.flightDealsProps?.minTransitDurationHr)
      ? props?.flightDealsProps?.minTransitDurationHr
      : '';
    const filterByMinTransitDurationHr = () => {
      if (checkValue(loadAllFlights, true) && checkValue(minTransitDurationHr)) {
        console.log('test', minTransitDurationHr, loadAllFlights);

        const checkLayoverInformation = (layoverInformation) => {
          if (!layoverInformation) {
            return false; // No value in layoverInformation
          }

          // Match days, hours, and minutes
          const regex = /(\d+d)?\s*(\d+h)?\s*(\d+m)?/;
          const match = layoverInformation.match(regex);

          if (!match) {
            return false; // No valid time found in layoverInformation
          }

          let totalMinutes = 0;

          // Convert days to minutes if present
          if (match[1]) {
            const days = parseInt(match[1], 10);
            totalMinutes += days * 24 * 60; // 1 day = 24 hours = 1440 minutes
          }

          // Convert hours to minutes if present
          if (match[2]) {
            const hours = parseInt(match[2], 10);
            totalMinutes += hours * 60; // 1 hour = 60 minutes
          }

          // Add minutes if present
          if (match[3]) {
            const minutes = parseInt(match[3], 10);
            totalMinutes += minutes;
          }

          return totalMinutes >= Number(minTransitDurationHr) * 60;
        };

        const getFilterByDurationHours = loadAllFlights.filter((flight) => {
          const transitDuration = get(flight, 'journeys[0].layoverInformation', '');
          console.log(
            'transitDuration',
            transitDuration,
            minTransitDurationHr,
            checkLayoverInformation(transitDuration)
          );
          return checkLayoverInformation(transitDuration);
        });

        return getFilterByDurationHours;
      } else {
        return loadAllFlights;
      }
    };

    loadAllFlights = filterByMinTransitDurationHr();

    console.log('loadChunkFlights - loadAllFlights', minTransitDurationHr, loadAllFlights);

    const [flightCount, setFlightCount] = useState(flightsCount);
    const isHasMoreFlights =
      checkValue(loadAllFlights) &&
      loadAllFlights?.length > 0 &&
      loadAllFlights?.length > props?.initialFlightsCount &&
      Number(maxFlightsCount) > Number(flightsCount);
    const [hasMoreFlights, setHasMoreFlights] = useState(isHasMoreFlights);

    flightsCount = flightCount;

    const loadChunkFlights = loadAllFlights.slice(0, flightCount);

    const loadMoreFlights = (): void => {
      flightsCount = Number(flightCount) + Number(props?.initialFlightsCount);
      setFlightCount(flightsCount);
      setHasMoreFlights(Number(maxFlightsCount) > Number(flightsCount));
      gaLogger.logGa('Flight widget', 'View more button click', 'View More Flights');
    };

    const showMoreTrack = () => {
      /* */
    };

    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const loardMoreProps: FlightLoadMoreProps = {
      analyticsCallBack: showMoreTrack,
      onClick: loadMoreFlights,
      domRef: showMoreButtonRef,
      ctaText: viewMoreCtaText,
    };

    const flightBookingButtonOnClick = (url: string, label: string) => {
      gaLogger.logGa('Flight widget', 'flight booking button click', `${label}`);
      window.open(url, '_blank');
    };

    const brandLogo = () => {
      return `https://${props?.context?.site?.domain}/_dms/header/logo.svg?locale=${props?.context?.locale}&siteid=${props?.context?.site?.id}`;
    };

    const setCarrierLogo = () => {
      return `${carrierLogo?.[0]?.url}`;
    };

    return (
      <>
        {checkValue(loadChunkFlights, true) ? (
          <>
            <UitkCard className="flights-cards" padded={true}>
              <UitkLayoutGrid justifyContent="center" columns={{ medium: 1, extra_large: 1, small: 1 }} space="half">
                <Viewport>
                  <ViewSmall>
                    <UitkLayoutGridItem>
                      <UitkSpacing padding={{ blockstart: 'six', inlinestart: 'three', inlineend: 'three' }}>
                        <UitkLayoutFlex direction="column" alignItems="center" space="half" wrap="wrap">
                          <UitkLayoutFlexItem>
                            <UitkHeaderBrandLogo altText="expedia logo" href="/" imgSrc={brandLogo()} />
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem alignSelf="center">
                            <UitkSpacing padding={{ blockstart: 'six', inlinestart: 'twelve', inlineend: 'twelve' }}>
                              <UitkHeading
                                align="center"
                                tag="h2"
                                className={
                                  checkValue(carrierLogo?.[0]?.url) && checkValue(carrierName) ? 'iscarrier' : ''
                                }
                                size={5}
                              >
                                {title}
                              </UitkHeading>
                            </UitkSpacing>
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem>
                            {carrierLogo?.[0]?.url && carrierName && (
                              <div className="carrier-wrapper">
                                <div className="carrier-logo">
                                  <UitkHeaderBrandLogo altText="expedia logo" href="/" imgSrc={setCarrierLogo()} />
                                </div>
                                <div className="carrier-name">{carrierName}</div>
                              </div>
                            )}
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem className="heading-seperator">
                            <hr />
                          </UitkLayoutFlexItem>
                        </UitkLayoutFlex>
                      </UitkSpacing>
                    </UitkLayoutGridItem>
                  </ViewSmall>
                  <ViewLarge>
                    <UitkLayoutGridItem className="flightdeals-header">
                      <UitkSpacing padding={{ blockstart: 'one', inlinestart: 'sixteen', inlineend: 'sixteen' }}>
                        <UitkLayoutFlex alignItems="center" space="half" wrap="wrap">
                          <UitkLayoutFlexItem className="flight-widget-logo" grow={2} minWidth="30x" maxWidth={110}>
                            <UitkHeaderBrandLogo altText="expedia logo" href="/" imgSrc={brandLogo()} />
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem grow={4} minWidth="48x">
                            <UitkSpacing padding={{ inlinestart: 'twelve' }}>
                              <UitkHeading tag="h2" className="override-heading-font-family" size={5}>
                                {title}
                              </UitkHeading>
                            </UitkSpacing>
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem>
                            <div className="carrier-wrapper">
                              <div className="carrier-logo">
                                <UitkHeaderBrandLogo altText="expedia logo" href="/" imgSrc={setCarrierLogo()} />
                              </div>
                              <div className="carrier-name">{carrierName}</div>
                            </div>
                          </UitkLayoutFlexItem>
                          <UitkLayoutFlexItem minWidth="full_width">
                            <hr />
                          </UitkLayoutFlexItem>
                        </UitkLayoutFlex>
                      </UitkSpacing>
                    </UitkLayoutGridItem>
                  </ViewLarge>
                </Viewport>

                <UitkLayoutGridItem>
                  <Viewport>
                    <ViewSmall>
                      <>
                        <UitkLayoutGrid
                          justifyContent="center"
                          columns={{ medium: 1, extra_large: 1, small: 1 }}
                          space="three"
                        >
                          <UitkLayoutGridItem>
                            <UitkList bullet="no-bullet" spacing={false}>
                              {loadChunkFlights?.map((list, cardIndex) => {
                                let flightUrl =
                                  list?.journeys?.[0]?.flightsJourneyAvailableFaresInformation?.applySelection
                                    ?.relativeURI?.value;
                                flightUrl = appendMcicid(
                                  flightUrl,
                                  mcicidTemplateType,
                                  mcicidCampaignUrl,
                                  mcicidMainUrl,
                                  mcicidReferrerId
                                );
                                const flightViewProps: FlightViewProps = {
                                  keyIndex: cardIndex,
                                  departureAndArrivalLocations: list?.journeys?.[0]?.departureAndArrivalLocations,
                                  airlines: list?.journeys?.[0]?.airlines,
                                  departureAndArrivalTime: list?.journeys?.[0]?.departureAndArrivalTime.completeText,
                                  differentDayArrival: list?.journeys?.[0]?.differentDayArrival,
                                  durationAndStops: list?.journeys?.[0]?.durationAndStops,
                                  label: list?.journeys?.[0]?.label,
                                  cabinClass: list?.journeys?.[0]?.cabinClass,
                                  layoverInformation: list?.journeys?.[0]?.layoverInformation,
                                  price: list.pricingInformation.price.completeText,
                                  ctaCopy: flightBookCtaCopy,
                                  ctaSubCopy: 'Fully refundable',
                                  journeyContinuationId: list.journeyContinuationId,
                                  tripType: list.pricingInformation.tripType,
                                  urgencyMessage: list?.journeys?.[0]?.urgencyMessage,
                                  flightButtonOnClick: () =>
                                    flightBookingButtonOnClick(flightUrl, list?.journeys?.[0]?.label),
                                  flightURL: flightUrl,
                                };
                                return (
                                  <>
                                    <FlightDealCardMobile {...flightViewProps} />
                                  </>
                                );
                              })}
                            </UitkList>
                          </UitkLayoutGridItem>
                        </UitkLayoutGrid>
                      </>
                    </ViewSmall>
                    <ViewLarge>
                      <>
                        <UitkLayoutGrid
                          justifyContent="center"
                          columns={{ medium: 1, extra_large: 1, small: 1 }}
                          space="three"
                        >
                          <UitkLayoutGridItem>
                            <UitkSpacing margin={{ blockstart: 'three', inlinestart: 'six', inlineend: 'six' }}>
                              <UitkList bullet="no-bullet" spacing={false}>
                                {loadChunkFlights?.map((list, cardIndex) => {
                                  let flightUrl =
                                    list?.journeys?.[0]?.flightsJourneyAvailableFaresInformation?.applySelection
                                      ?.relativeURI?.value;
                                  flightUrl = appendMcicid(
                                    flightUrl,
                                    mcicidTemplateType,
                                    mcicidCampaignUrl,
                                    mcicidMainUrl,
                                    mcicidReferrerId
                                  );

                                  const flightViewProps: FlightViewProps = {
                                    keyIndex: cardIndex,
                                    departureAndArrivalLocations: list?.journeys?.[0]?.departureAndArrivalLocations,
                                    airlines: list?.journeys?.[0]?.airlines,
                                    departureAndArrivalTime: list?.journeys?.[0]?.departureAndArrivalTime.completeText,
                                    differentDayArrival: list?.journeys?.[0]?.differentDayArrival,
                                    durationAndStops: list?.journeys?.[0]?.durationAndStops,
                                    label: list?.journeys?.[0]?.label,
                                    cabinClass: list?.journeys?.[0]?.cabinClass,
                                    layoverInformation: list?.journeys?.[0]?.layoverInformation,
                                    price: list.pricingInformation.price.completeText,
                                    ctaCopy: flightBookCtaCopy,
                                    ctaSubCopy: 'Fully refundable',
                                    journeyContinuationId: list.journeyContinuationId,
                                    tripType: list.pricingInformation.tripType,
                                    urgencyMessage: list?.journeys?.[0]?.urgencyMessage,
                                    flightButtonOnClick: () =>
                                      flightBookingButtonOnClick(flightUrl, list?.journeys?.[0]?.label),
                                    flightURL: flightUrl,
                                  };
                                  return (
                                    <>
                                      <FlightDealCard {...flightViewProps} />
                                    </>
                                  );
                                })}
                              </UitkList>
                            </UitkSpacing>
                          </UitkLayoutGridItem>
                        </UitkLayoutGrid>
                      </>
                    </ViewLarge>
                  </Viewport>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </UitkCard>
            <UitkLayoutGrid>
              <UitkLayoutGridItem>
                {hasMoreFlights && (
                  <UitkSpacing margin={{ blockstart: 'six', inlinestart: 'six', inlineend: 'six' }}>
                    <FlightLoardMore {...loardMoreProps} />
                  </UitkSpacing>
                )}
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
          </>
        ) : (
          <>
            <div className="flight-deals no-flight-deals">
              <UitkIcon name="search" size={UitkIconSize.LARGE} />
              <UitkHeading align="center" tag="h2" className="no-flights" size={5}>
                {"Sorry, we couldn't find any flights that match your criteria"}
              </UitkHeading>
            </div>
          </>
        )}
      </>
    );
  })
);

export const FlightHighLights: React.FC<FlightHighLightsProps> = (props) => {
  const { themeType = 'inverse', highlightText } = props;
  return (
    <>
      {checkValue(highlightText, true) && (
        <>
          <UitkSpacing margin={{ blockstart: 'six', inlinestart: 'unset', inlineend: 'unset' }}>
            <UitkList
              className="flight-highlight"
              orientation="inline"
              bullet="icon-alternate"
              type="ordered"
              spacing={false}
            >
              {highlightText?.map((list, cardIndex) => {
                return (
                  <>
                    <UitkListItem key={`listitem-${cardIndex}`}>
                      <UitkText size={500} weight="bold" theme={themeType}>
                        <UitkIcon name="check" />
                      </UitkText>
                      <UitkText size={500} weight="bold" theme={themeType}>
                        {list}
                      </UitkText>
                    </UitkListItem>
                  </>
                );
              })}
            </UitkList>
          </UitkSpacing>
        </>
      )}
    </>
  );
};

export const FlightLoardMore: React.FC<FlightLoadMoreProps> = (props) => {
  return (
    <>
      <UitkSpacing padding={{ blockstart: 'six', blockend: 'six' }}>
        <UitkLayoutFlex justifyContent="center" className="load-more-deals-button">
          <UitkLayoutFlexItem>
            <UitkSecondaryButton
              className={'show-more ga4-deals-view-more'}
              name="showMoreButton"
              type="button"
              size={UitkButtonSize.LARGE}
              onClick={props.onClick}
              domRef={props.domRef}
              analytics={{
                id: `${mcicidFormatText}`,
                callback: props.analyticsCallBack,
              }}
            >
              {props.ctaText}
            </UitkSecondaryButton>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkSpacing>
    </>
  );
};
